import React from 'react';

import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';

import HandsonArticleLayout from './components/HandsonArticleLayout';
import WorkflowChatBubble from './components/WorkflowChatBubble';
import WorkflowChecklist from './components/WorkflowChecklist';
import WorkflowChevronArrow from './components/WorkflowChevronArrow';
import WorkflowCodeBlock from './components/WorkflowCodeBlock';
import WorkflowDirectoryTree from './components/WorkflowDirectoryTree';
import WorkflowGoalBox from './components/WorkflowGoalBox';
import WorkflowInfoBox from './components/WorkflowInfoBox';
import WorkflowLinkCard from './components/WorkflowLinkCard';
import WorkflowList from './components/WorkflowList';
import WorkflowLockedCodeBlock from './components/WorkflowLockedCodeBlock';
import WorkflowPracticeBox from './components/WorkflowPracticeBox';
import WorkflowProcedureFlow from './components/WorkflowProcedureFlow';
import WorkflowSection from './components/WorkflowSection';
import WorkflowTable from './components/WorkflowTable';
import WorkflowTableOfContents from './components/WorkflowTableOfContents';
import WorkflowText from './components/WorkflowText';
import WorkflowTipBox from './components/WorkflowTipBox';

const seriesSteps = [
  {
    label: 'structure/tech',
    title: 'structure/techルールの作成',
    path: '/devdocs/handson/structure-tech',
  },
  { label: 'スキーマ+API仕様書', title: 'スキーマ定義とAPI仕様書の生成', active: true },
  {
    label: 'API実装ワークフロー',
    title: 'API実装ワークフローを作成する',
    path: '/devdocs/handson/api-workflow',
  },
  { label: 'E2Eテスト', title: 'E2Eテストを実行する', path: '/devdocs/handson/e2e' },
];

const HandsonSchemaApi: React.FC = () => {
  return (
    <HandsonArticleLayout
      title="実装編 — スキーマ定義 + API仕様書の生成"
      seriesName="実装編"
      tags={['Hands-on', 'Schema', 'API Spec', 'PDCA']}
      seriesSteps={seriesSteps}
      prevLink={{ label: 'structure/techルールの作成', path: '/devdocs/handson/structure-tech' }}
      nextLink={{ label: 'API実装ワークフロー', path: '/devdocs/handson/api-workflow' }}
    >
      <WorkflowGoalBox
        prerequisites={['「実装編 — structure/techルールの作成」が完了している']}
        completionCriteria={[
          '.kiro/steering/create_schema.md が作成されている',
          '.kiro/steering/api_specs_rule.md が作成されている',
          'docs/schema/ 配下にスキーマ定義書が生成されている',
          'docs/api/ 配下にAPI仕様書が生成されている',
          'src/main/resources/db/migration/ にFlywayマイグレーションSQLが生成されている',
          'セクション5のチェックリスト全項目に合格している',
          '少なくとも1回はルール改善→再生成のサイクルを経験している',
        ]}
      >
        スキーマ定義とAPI仕様書の生成ルールを作成し、usecases.md / proposal.md
        からAIで基本設計書を生成する。PDCAサイクルでルールを改善しながら品質を上げる。
      </WorkflowGoalBox>

      <WorkflowLinkCard
        title="実装編 — structure/techルールの作成"
        description="前の記事"
        path="/devdocs/handson/structure-tech"
      />

      <WorkflowTableOfContents
        items={[
          { id: 'section-1', label: 'この章で体験すること' },
          { id: 'section-2', label: 'スキーマ定義とAPI仕様書の役割' },
          { id: 'section-3', label: 'Steeringルールを作成する' },
          { id: 'section-4', label: 'AIでスキーマ定義・API仕様書を生成する' },
          { id: 'section-5', label: 'チェックリストで品質を確認する' },
          { id: 'section-6', label: 'ルールを改善して再生成する' },
          { id: 'section-7', label: '現在のプロジェクト構成' },
          { id: 'section-8', label: 'ベストプラクティスとアンチパターン' },
          { id: 'section-9', label: 'この章で学んだこと' },
        ]}
      />

      {/* セクション1 */}
      <WorkflowSection title="この章で体験すること" id="section-1" number={1}>
        <WorkflowSection title="要件定義の成果物を基本設計に変換する" subNumber="1.1">
          <WorkflowText>
            要件定義フェーズで作った proposal.md（テーブル設計・API設計の決定事項）と
            usecases.md（システムユースケース）から、実装に直接使える基本設計書を生成します。
          </WorkflowText>

          <WorkflowProcedureFlow
            steps={[
              {
                label: '入力（要件定義の成果物）',
                sub: 'todo.proposal.md / todo.usecases.md',
                variant: 'external',
              },
              {
                label: '出力（基本設計書）',
                sub: 'スキーマ定義書（テーブル定義 .md / マイグレーション .sql）+ API仕様書',
              },
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="PDCAサイクル — 再び" subNumber="1.2">
          <WorkflowText>
            「要件定義編 —
            proposal/business生成」と同じ「ルール→生成→チェック→改善」のサイクルです。対象が変わっただけで、やることは同じです。
          </WorkflowText>

          <WorkflowLinkCard
            title="要件定義編 — proposal/business生成"
            description="PDCAサイクルの初回体験"
            path="/devdocs/handson/proposal-business"
          />

          <Grid container spacing={1} sx={{ maxWidth: 480, mx: 'auto', my: 2 }}>
            <Grid size={5}>
              <WorkflowChevronArrow
                label="Plan"
                sub="ルール作成"
                bgcolor="#1565c0"
                borderColor="#0d47a1"
                sx={{ height: '100%' }}
              />
            </Grid>
            <Grid size={2} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ArrowForwardIcon sx={{ fontSize: 22, color: 'text.disabled' }} />
            </Grid>
            <Grid size={5}>
              <WorkflowChevronArrow
                label="Do"
                sub="AI生成"
                bgcolor="#2e7d32"
                borderColor="#1b5e20"
                sx={{ height: '100%' }}
              />
            </Grid>
            <Grid size={5} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ArrowUpwardIcon sx={{ fontSize: 22, color: 'text.disabled' }} />
            </Grid>
            <Grid size={2} />
            <Grid size={5} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ArrowDownwardIcon sx={{ fontSize: 22, color: 'text.disabled' }} />
            </Grid>
            <Grid size={5}>
              <WorkflowChevronArrow
                label="Act"
                sub="ルール改善"
                bgcolor="#e65100"
                borderColor="#bf360c"
                sx={{ height: '100%' }}
              />
            </Grid>
            <Grid size={2} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ArrowForwardIcon
                sx={{ fontSize: 22, color: 'text.disabled', transform: 'rotate(180deg)' }}
              />
            </Grid>
            <Grid size={5}>
              <WorkflowChevronArrow
                label="Check"
                sub="品質確認"
                bgcolor="#f57f17"
                borderColor="#e65100"
                sx={{ height: '100%' }}
              />
            </Grid>
          </Grid>
        </WorkflowSection>

        <WorkflowSection title="この章の流れ" subNumber="1.3">
          <WorkflowList
            variant="ordered"
            items={[
              'スキーマ定義・API仕様書の役割を確認する',
              'Steeringルールを作成する（create_schema.md / api_specs_rule.md）',
              'AIでスキーマ定義・API仕様書を生成する',
              'Flywayマイグレーションを生成する',
              'チェックリストで品質を確認する → 不合格箇所を改善',
              'ルールを改善して再生成する → 合格するまで繰り返す',
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="参照する記事" subNumber="1.4">
          <WorkflowText>
            以下のAIと作る、壊れないバックエンド紹介記事に、スキーマ定義・API仕様書の詳細なフォーマット・記載ルール・完成例が掲載されています。この章を進める前に目を通してください。
          </WorkflowText>

          <WorkflowLinkCard
            title="バックエンド開発編 Part1 — スキーマ定義とAPI仕様書"
            description="スキーマ定義・API仕様書のフォーマット・命名規則・Steeringルール全文"
            path="/devdocs/workflow/be-step1"
          />

          <WorkflowTipBox title="紹介記事の「用語集」「Rv指摘表」について">
            <WorkflowText>
              紹介記事には「用語集（論理名⇔物理名の対応表）」や「Rv指摘表（レビュー指摘の蓄積）」の仕組みが登場します。これらは個人開発では一定の品質向上効果がありますが、このハンズオンでは必須としていません。
            </WorkflowText>
            <WorkflowText>
              理由は、チーム開発での運用方法がまだ確立されていないためです。
            </WorkflowText>
            <WorkflowList
              items={[
                '用語集: 複数人が同時に物理名を追加すると、コンフリクトや不整合な命名が混入するリスクがある',
                'Rv指摘表: 複数レビュアーが同時に記録すると同様の競合が発生しうる',
              ]}
            />
            <WorkflowText>
              いずれも個人開発では問題になりませんが、チーム開発に導入する際の運用ルール（誰が管理するか、マージ戦略、命名権限等）が定まっておらず、現時点では現場に導入できるレベルに達していないと考えています。
            </WorkflowText>
            <WorkflowText>
              興味がある方は紹介記事を参考に、このハンズオンで自分のプロジェクトに組み込んでみても構いません。個人開発では十分に効果を発揮するので、試してみる価値はあります。
            </WorkflowText>
          </WorkflowTipBox>
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション2 */}
      <WorkflowSection title="スキーマ定義とAPI仕様書の役割" id="section-2" number={2}>
        <WorkflowSection title="なぜ基本設計書を作るのか" subNumber="2.1">
          <WorkflowTipBox title="設計書なしでコード生成するとどうなるか">
            <WorkflowText>
              「proposal.mdとusecases.mdがあるなら、そこから直接コードを生成できるのでは？」と思うかもしれません。
            </WorkflowText>
            <WorkflowText>確かに可能ですが、以下の問題が起きます:</WorkflowText>
            <WorkflowList
              items={[
                'AIのコンテキスト限界: proposal + usecases + structure + tech を全て読み込んだ上でコードを生成すると、コンテキスト枠を大量に消費する。1APIずつ仕様書ベースで生成する方がはるかに精度が高い',
                '増分修正が困難: APIのRequest/Responseを修正したい時、proposalを読み直す必要がある。仕様書があれば仕様書だけ修正すればよい',
                'テストの根拠が曖昧: テストケースの期待値を「何をもとに」判断するか。API仕様書があればRequest/Responseが明確に定義されている',
              ]}
            />
            <WorkflowText>
              設計書 = AIへの仕様伝達手段 兼
              品質検証の根拠です。手間を惜しまず作ることで後続ステップの品質が安定します。
            </WorkflowText>
          </WorkflowTipBox>
        </WorkflowSection>

        <WorkflowSection title="何を作るか" subNumber="2.2">
          <WorkflowTable
            headers={['成果物', '配置先', '内容']}
            rows={[
              ['スキーマ定義書', 'docs/schema/', 'テーブル定義（カラム・型・制約・index）'],
              ['API仕様書', 'docs/api/', 'Request/Response/エラー/処理概要'],
              [
                'Flywayマイグレーション',
                'src/main/resources/db/migration/',
                'CREATE TABLE / INSERT等のSQL',
              ],
            ]}
          />
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション3 */}
      <WorkflowSection title="Steeringルールを作成する" id="section-3" number={3}>
        <WorkflowSection title="何を作るか" subNumber="3.1">
          <WorkflowText>
            スキーマ定義とAPI仕様書をAIに生成させるためのSteeringルールを2ファイル作成します。
          </WorkflowText>
          <WorkflowTable
            headers={['ファイル', '役割', '読み込みモード']}
            rows={[
              [
                '.kiro/steering/create_schema.md',
                'スキーマ定義書の生成ルール',
                'fileMatch（docs/schema/**/*.md）',
              ],
              [
                '.kiro/steering/api_specs_rule.md',
                'API仕様書の記載ルール',
                'fileMatch（docs/api/**/*.md）',
              ],
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="書くべき情報" subNumber="3.2">
          <WorkflowText>
            各ルールに書くべき情報の詳細は、セクション1で紹介したAIと作る、壊れないバックエンド紹介記事（バックエンド開発編
            Part1）に全て掲載されています。紹介記事のスキーマ定義ルール・API仕様書ルールを参考に、TODOアプリに必要な範囲をシンプルに書いてください。
          </WorkflowText>

          <WorkflowTipBox title="なぜ2ファイルに分けるのか">
            <WorkflowText>
              スキーマ定義とAPI仕様書はファイルの配置先が異なります（docs/schema/ vs
              docs/api/）。fileMatchのglobパターンが異なるため、Steeringファイルを分ける方が自然です。
            </WorkflowText>
            <WorkflowList
              items={[
                'create_schema.md → docs/schema/**/*.md にマッチ',
                'api_specs_rule.md → docs/api/**/*.md にマッチ',
              ]}
            />
            <WorkflowText>
              スキーマ定義だけ修正したい時にAPI仕様書のルールまで読み込まれるのはコンテキストの無駄です。
            </WorkflowText>
          </WorkflowTipBox>
        </WorkflowSection>

        <WorkflowSection title="自分で書いてみる" subNumber="3.3">
          <WorkflowText>以下の2ファイルを作成してください。</WorkflowText>
          <WorkflowList
            items={['.kiro/steering/create_schema.md', '.kiro/steering/api_specs_rule.md']}
          />

          <WorkflowInfoBox type="warning">
            <Typography variant="body2">
              <strong>注意</strong>:
              紹介記事のルールはKanzhong固有の要素（権限一覧連動、API一覧連動、DD書リンク等）を含んでいます。TODOアプリではこれらは不要です。
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              以下を意識してシンプルに書いてください:
            </Typography>
          </WorkflowInfoBox>

          <WorkflowList
            items={[
              '認証なし → 権限セクションは不要',
              'DD書連動は「実装編 — API実装ワークフロー」で扱う → この段階では不要',
              'API一覧は必須ではない → 必要なら別途追加',
            ]}
          />

          <WorkflowLinkCard
            title="実装編 — API実装ワークフロー"
            description="DD書作成を含むワークフロー"
            path="/devdocs/handson/api-workflow"
          />
        </WorkflowSection>

        <WorkflowSection title="create_schema.md - 完成例" subNumber="3.4">
          <WorkflowLockedCodeBlock password="Bw6tNm">
            {`---
inclusion: fileMatch
globs:
  - "docs/schema/**/*.md"
  - "src/main/resources/db/migration/**/*.sql"
---

# スキーマ定義ルール

## ファイル命名規則

- 配置先: docs/schema/
- ファイル名: {連番}__{テーブル物理名}.md
- 連番: 3桁ゼロ埋め（001, 002, ...）
- 例: 001__todo.md, 002__todo_assignee.md

## ドキュメント構造

スキーマ定義書は以下のセクションで構成する。

### 必須セクション

1. # テーブル定義 — ドキュメントタイトル（固定）
2. ## テーブル名 — 論理名と物理名のテーブル
3. ## 概要 — テーブルの説明（1〜2文）
4. ## テーブル定義 — カラム定義テーブル
5. ## unique key — ユニークキー定義（なければ「なし」）
6. ## index — インデックス定義（なければ「なし」）

## テンプレート

# テーブル定義

## テーブル名

| 論理名 | 物理名 |
|--------|--------|
| {論理名} | {物理名} |

## 概要

{テーブルの説明}

## テーブル定義

| 論理名 | 物理名 | 種別 | Not Null | PK | 備考 |
|--------|--------|------|----------|----|----- |
| ... | ... | ... | ... | ... | ... |

## unique key

なし

## index

| index名 | column | seq |
|---------|--------|-----|
| ... | ... | ... |

## カラム定義の記述ルール

| 項目 | ルール |
|------|--------|
| 論理名 | 日本語で記載 |
| 物理名 | snake_case（小文字） |
| 種別 | MySQL 8.0 のデータ型（varchar(n), int, bigint, datetime, tinyint(1), text, decimal(m,n)） |
| Not Null | NOT NULL制約がある場合は 〇 を記載 |
| PK | 主キーの場合は 〇 を記載 |
| 備考 | UUID、AUTO_INCREMENT等の補足情報 |

## unique key / index のルール

- ユニークキー名: {テーブル名}_{カラム名}_uk
- インデックス名: {テーブル名}_{カラム名}_idx
- 複合インデックスの場合は同じindex名で seq を連番にする

## Flywayマイグレーション連携

スキーマ定義書を新規作成・修正した場合は、対応するFlywayマイグレーションSQLファイルも作成・更新する。

### マイグレーションファイルの命名規則

- 配置先: src/main/resources/db/migration/
- ファイル名: V{連番}__{説明}.sql
- 連番: 整数の連番（1, 2, 3, ...）
- 説明: 操作内容をsnake_caseで記述
- 例: V1__create_todo.sql, V2__create_todo_assignee.sql, V3__add_priority_to_todo.sql

### テーブル新規作成時

- ENGINE=InnoDB DEFAULT CHARSET=utf8mb4 COLLATE=utf8mb4_general_ci を末尾に付与
- PRIMARY KEY はCREATE TABLE文内で定義する
- UNIQUE KEY はCREATE TABLE文内で定義する
- INDEX はCREATE TABLE文内で定義する

### スキーマ定義の修正時

スキーマ定義書のカラム追加・変更・削除が発生した場合、新しい連番のマイグレーションファイルを追加する（既存ファイルは書き換えない）。

- カラム追加: ALTER TABLE {テーブル} ADD COLUMN ...
- カラム変更: ALTER TABLE {テーブル} MODIFY COLUMN ...
- カラム削除: ALTER TABLE {テーブル} DROP COLUMN ...
- INDEX追加: CREATE INDEX ...
- INDEX削除: ALTER TABLE {テーブル} DROP INDEX ...

### 整合性ルール

- 全マイグレーションファイルを順に適用した結果が、スキーマ定義書の最新状態と一致すること
- 不一致がある場合はマイグレーションファイルを修正する

## proposal.md との整合性

- カラム名・型・Not Null は proposal.md のテーブル定義と完全に一致させる
- proposal.md に存在しないカラムを追加しない
- 不一致がある場合は proposal.md を正とする`}
          </WorkflowLockedCodeBlock>
        </WorkflowSection>

        <WorkflowSection title="api_specs_rule.md - 完成例" subNumber="3.5">
          <WorkflowLockedCodeBlock password="Yj8kRe">
            {`---
inclusion: fileMatch
globs:
  - "docs/api/**/*.md"
---

# API仕様書ルール

## ファイル命名規則

- 配置先: docs/api/
- ファイル名: API{ID}_{API名}.md
- API IDは proposal.md のAPI一覧と一致させる
- 例: API001_TODO登録.md, API002_TODO更新.md

## ドキュメント構造

API仕様書は以下のセクションで構成する。

### テンプレート

# API{ID} {API名}

## 概要

| API ID | API名 | API概要 | Method | Endpoint |
|--------|-------|---------|--------|----------|
| API{ID} | {API名} | {概要} | {Method} | {Endpoint} |

## Request

### Body

| 論理名 | 物理名 | type | NotNull | max | format | 備考 |
|--------|--------|------|---------|-----|--------|------|
| ... | ... | ... | ... | ... | ... | ... |

## Response

### Status Code

| Error | Code | 備考 |
|-------|------|------|
| 成功 | 200 | |
| バリデーションエラー | 400 | |
| 対象なし | 404 | 該当リソースが存在しない |

### Body

| 論理名 | 物理名 | type | format | NotNull | 備考 |
|--------|--------|------|--------|---------|------|
| ... | ... | ... | ... | ... | ... |

## 参照DB

- [{テーブル論理名}](../schema/{ファイル名})

## 処理概要

1. {処理ステップ1}
2. {処理ステップ2}
3. ...

## Request/Response 記載ルール

### 型の表記

| type | 用途 |
|------|------|
| string | 文字列 |
| int | 整数 |
| long | 長整数 |
| boolean | 真偽値 |
| datetime | 日時 |
| object | ネストオブジェクト |
| object[] | オブジェクト配列 |

### ネスト時の表記

- オブジェクトがネストする場合、type は object または object[] とする
- ネストした項目は &emsp; でインデントを表現する

### レスポンスのルール

- list型のレスポンスはnullを返却しない。0件でも空リストを返却する
- 1件取得APIのレスポンスは result (object) でネストする
- 複数件取得APIのレスポンスは results (object[]) でネストする
- 日時のformatは yyyy-MM-dd'T'HH:mm:ss とする

### パスパラメータ

- パスパラメータがある場合、Endpointに {パラメータ名} を含める
- Request内に ### パスパラメータ セクションを追加する
- パスパラメータのテーブルは 論理名, 物理名, type, NotNull, 備考 の5列

## 処理概要の書き方

- 具体的な処理ステップを番号付きリストで記載する
- 「○○を取得する」「○○を登録する」のように動詞で記述する
- 条件分岐がある場合は明記する
- エラー判定（存在チェック、重複チェック等）も記載する
- 処理概要は後続のコード生成で参照されるため、曖昧にしない

## proposal.md との整合性

- API ID / Endpoint / Method は proposal.md のAPI一覧と完全に一致させる
- Request/Response の項目は proposal.md のAPI概要と一致させる
- proposal.md に記載のないAPIを追加しない

## チェックリスト

API仕様書を生成した後、以下を確認する:

- [ ] 全APIにRequest/Response/Status Code/処理概要が記載されているか
- [ ] proposal.md のAPI一覧と ID/Endpoint/Method が一致しているか
- [ ] Request の型・NotNull がスキーマ定義と整合しているか
- [ ] Response の型・NotNull がスキーマ定義と整合しているか
- [ ] パスパラメータがある場合、Endpointとパスパラメータセクションが一致しているか
- [ ] 処理概要が「誰が読んでもコードが書ける」レベルに具体的か
- [ ] Status Code にバリデーションエラー(400)と対象なし(404)が含まれているか（該当する場合）`}
          </WorkflowLockedCodeBlock>
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション4 */}
      <WorkflowSection title="AIでスキーマ定義・API仕様書を生成する" id="section-4" number={4}>
        <WorkflowSection title="生成の順序" subNumber="4.1">
          <WorkflowInfoBox type="important">
            <Typography variant="body2" fontWeight={600}>
              重要: スキーマ定義を先に生成し、その後にAPI仕様書を生成してください。
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              API仕様書のRequest/Responseはスキーマ定義のカラムに基づきます。スキーマが確定していない状態でAPI仕様書を書くと、型やカラム名が食い違います。
            </Typography>
          </WorkflowInfoBox>
        </WorkflowSection>

        <WorkflowSection title="スキーマ定義の生成" subNumber="4.2">
          <WorkflowText>
            proposal.md のテーブル定義セクションと usecases.md
            を参照して、スキーマ定義書を生成します。
          </WorkflowText>
          <WorkflowChatBubble role="human">
            {`#todo.proposal.md #todo.usecases.md を参照して、スキーマ定義書を docs/schema/ に作成してください。
マイグレーションSQLも合わせて作成してください。`}
          </WorkflowChatBubble>
        </WorkflowSection>

        <WorkflowSection title="API仕様書の生成" subNumber="4.3">
          <WorkflowText>スキーマ定義が完了したら、API仕様書を生成します。</WorkflowText>
          <WorkflowChatBubble role="human">
            {`#todo.proposal.md と #todo.usecases.md を参照して、
全APIのAPI仕様書を docs/api/ に作成してください。`}
          </WorkflowChatBubble>

          <WorkflowTipBox title="一度に全APIを生成するか、1つずつ生成するか">
            <WorkflowText>
              APIの数が少ない（5個以下）なら一括生成で問題ありません。TODOアプリのCRUD（登録・取得・一覧取得・更新・削除）は共通のパターンが多いので、一括生成の方が効率的です。
            </WorkflowText>
            <WorkflowText>
              一方、APIが多い場合やAPIごとに仕様が大きく異なる場合は、1〜2個ずつ生成する方が品質が安定します。
            </WorkflowText>
            <WorkflowText>判断基準:</WorkflowText>
            <WorkflowList items={['共通パターンが多い → 一括生成', '個別仕様が複雑 → 分割生成']} />
          </WorkflowTipBox>
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション5 */}
      <WorkflowSection title="チェックリストで品質を確認する" id="section-5" number={5}>
        <WorkflowSection title="スキーマ定義チェックリスト" subNumber="5.1">
          <WorkflowChecklist
            groups={[
              {
                title: 'スキーマ定義チェックリスト',
                items: [
                  '全テーブルが docs/schema/ に配置されているか',
                  'ファイル名が命名規則（{連番}__{物理名}.md）に従っているか',
                  'proposal.md のテーブル定義と全カラムが一致しているか（漏れ・余分なカラムがないか）',
                  'カラムの型・Not Null が proposal.md と一致しているか',
                  'PKが正しく設定されているか',
                  '対応するFlywayマイグレーションSQLが存在するか',
                  'マイグレーションSQLの型がスキーマ定義書と一致しているか',
                  'ENGINE=InnoDB DEFAULT CHARSET=utf8mb4 が付与されているか',
                ],
              },
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="API仕様書チェックリスト" subNumber="5.2">
          <WorkflowChecklist
            groups={[
              {
                title: 'API仕様書チェックリスト',
                items: [
                  'proposal.md のAPI一覧に含まれる全APIが作成されているか',
                  'API ID / Endpoint / Method が proposal.md と一致しているか',
                  'Request の項目が proposal.md のAPI概要と一致しているか',
                  'Response の項目が proposal.md のAPI概要と一致しているか',
                  'Request の型がスキーマ定義の型と整合しているか',
                  'Status Code にバリデーションエラー(400)が含まれているか',
                  '削除・更新・取得APIのStatus Codeに対象なし(404)が含まれているか',
                  '処理概要が具体的に記載されているか（「〇〇する」程度は不合格）',
                  '1件取得APIのレスポンスが result でラップされているか',
                  '複数件取得APIのレスポンスが results でラップされているか',
                ],
              },
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="不合格パターンと改善ケーススタディ" subNumber="5.3">
          <WorkflowText>
            「要件定義編 —
            proposal/business生成」と同様、チェック不合格時は原因を切り分けて対処します。
          </WorkflowText>

          <WorkflowLinkCard
            title="要件定義編 — proposal/business生成"
            description="不合格パターンと改善ケーススタディ"
            path="/devdocs/handson/proposal-business"
          />

          <WorkflowTable
            headers={['不合格パターン', '原因', '対処']}
            rows={[
              ['proposal不足', 'proposalに情報が足りない', 'proposal.md を補完してから再生成'],
              ['ルール不足', 'AIがフォーマットを間違える', 'ルールを改善する'],
              ['部分ミス', '一部のAPIだけ間違っている', 'AIに修正指示を出す'],
            ]}
          />

          <WorkflowTable
            headers={['#', '症状', '原因', '改善']}
            rows={[
              [
                '1',
                'スキーマ定義に proposal にないカラムが追加されている',
                '禁止事項に「proposalに存在しないカラムを追加しない」がない',
                '禁止事項に追記する',
              ],
              [
                '2',
                'API仕様書の処理概要が「TODOを登録する」の1行',
                '処理概要の書き方ルールが抽象的',
                '「バリデーション→重複チェック→INSERT→レスポンス返却 のようにステップを分ける」と具体化する',
              ],
              [
                '3',
                'レスポンスがresultでラップされていない',
                'ルールにはあるがAIが無視した',
                '一度修正指示で直し、再発するようなら禁止事項に追記する',
              ],
              [
                '4',
                'マイグレーションSQLのCHARSET指定がない',
                'ルールに記載はあるが見落とした',
                'テンプレートにSQLの具体例を1つ追加する（例があるとAIの遵守率が上がる）',
              ],
            ]}
          />
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション6 */}
      <WorkflowSection title="ルールを改善して再生成する" id="section-6" number={6}>
        <WorkflowSection title="改善の手順" subNumber="6.1">
          <WorkflowText>
            「要件定義編 — proposal/business生成」セクション5と同じ手順です。
          </WorkflowText>

          <WorkflowLinkCard
            title="要件定義編 — proposal/business生成"
            description="ルール改善の手順"
            path="/devdocs/handson/proposal-business"
          />

          <WorkflowList
            variant="ordered"
            items={[
              '不合格の原因を特定する',
              'ルールのどこを直すか決める',
              'Steeringファイルを修正する',
              '再生成する',
              '再度チェックする',
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="再生成のプロンプト例" subNumber="6.2">
          <WorkflowChatBubble role="human">
            {`create_schema.md / api_specs_rule.md のルールを更新しました。
更新したルールに従って、スキーマ定義書とAPI仕様書を再生成してください。`}
          </WorkflowChatBubble>

          <WorkflowInfoBox type="warning">
            <Typography variant="body2">
              <strong>注意</strong>:
              スキーマ定義を修正した場合は、マイグレーションSQLとAPI仕様書のRequest/Responseも連動して修正が必要です。スキーマ→マイグレーション→API仕様書の順で影響が波及します。
            </Typography>
          </WorkflowInfoBox>
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション7 */}
      <WorkflowSection title="現在のプロジェクト構成" id="section-7" number={7}>
        <WorkflowDirectoryTree
          root="team-todo/"
          entries={[
            {
              name: '.kiro',
              color: '#1565c0',
              children: [
                {
                  name: 'requirements',
                  children: [
                    {
                      name: 'todo',
                      children: [{ name: 'todo.rfp.md' }, { name: 'todo.proposal.md' }],
                    },
                    {
                      name: 'usecases',
                      children: [{ name: 'todo.business.md' }, { name: 'todo.usecases.md' }],
                    },
                  ],
                },
                {
                  name: 'steering',
                  children: [
                    { name: 'product.md' },
                    { name: 'create_proposal.md' },
                    { name: 'create_business.md' },
                    { name: 'create_usecases.md' },
                    { name: 'create_product.md' },
                    { name: 'structure.md' },
                    { name: 'tech.md' },
                    { name: 'create_schema.md', desc: '今回作成', highlight: '#e65100' },
                    { name: 'api_specs_rule.md', desc: '今回作成', highlight: '#e65100' },
                  ],
                },
              ],
            },
            {
              name: 'docs',
              color: '#2e7d32',
              children: [
                {
                  name: 'schema',
                  desc: '今回生成',
                  highlight: '#1976d2',
                  children: [{ name: '001__todo.md' }, { name: '...' }],
                },
                {
                  name: 'api',
                  desc: '今回生成',
                  highlight: '#1976d2',
                  children: [
                    { name: 'API001_TODO登録.md' },
                    { name: 'API002_TODO更新.md' },
                    { name: '...' },
                  ],
                },
              ],
            },
            {
              name: 'src',
              children: [
                {
                  name: 'main',
                  children: [
                    {
                      name: 'java/com/example/teamtodo',
                      children: [
                        { name: 'TeamTodoApplication.java' },
                        {
                          name: 'config',
                          children: [{ name: 'SecurityConfig.java' }],
                        },
                      ],
                    },
                    {
                      name: 'resources',
                      children: [
                        { name: 'application.yml' },
                        {
                          name: 'db/migration',
                          desc: '今回生成',
                          highlight: '#1976d2',
                          children: [{ name: 'V1__create_todo.sql' }, { name: '...' }],
                        },
                      ],
                    },
                  ],
                },
                { name: 'test/' },
              ],
            },
            { name: 'build.gradle' },
            { name: 'settings.gradle' },
            { name: 'docker-compose.yml' },
            { name: '...' },
          ]}
        />
      </WorkflowSection>

      {/* セクション8 */}
      <WorkflowSection title="ベストプラクティスとアンチパターン" id="section-8" number={8}>
        <WorkflowPracticeBox
          type="best"
          items={[
            {
              practice: 'スキーマ定義→API仕様書の順で生成する',
              effect: 'カラム名・型が確定してからRequest/Responseを定義する方が整合性が取れる',
            },
            {
              practice: 'proposal.md を Single Source of Truth にする',
              effect:
                'I/Fを修正したいときはproposalから修正して再生成する。生成物を直接書き換えない',
            },
            {
              practice: '処理概要を具体的に書く',
              effect:
                '「〇〇する」程度では後続のコード生成で品質が下がる。判定条件・エラーケースを明記する',
            },
            {
              practice: 'マイグレーションSQLをスキーマ定義と連動させる',
              effect: 'スキーマ定義だけ直してSQLを放置するとDB構築時にエラーになる',
            },
            {
              practice: 'チェック後にルールを改善する',
              effect: '同じ問題が次回も起きないよう、根本原因をルールに反映する',
            },
            {
              practice: 'fileMatchのglobを正しく設定する',
              effect:
                'スキーマ定義操作時にスキーマルール、API仕様書操作時にAPIルールが自動で読み込まれる',
            },
          ]}
        />

        <WorkflowPracticeBox
          type="anti"
          items={[
            {
              practice: 'API仕様書を先に作り、後からスキーマ定義を作る',
              effect:
                'Request/Responseの型がテーブル定義と食い違う。スキーマ定義→API仕様書の順で作成する',
            },
            {
              practice: '生成されたAPI仕様書を直接手書き修正する',
              effect:
                'proposalとの乖離が生じ、次回再生成時に上書きされる。proposalを修正して再生成する',
            },
            {
              practice: '処理概要を空欄にする',
              effect:
                '後続のコード生成フェーズでAIが処理を推測し、仕様と異なるコードになる。処理ステップ・条件分岐・エラー判定を具体的に記載する',
            },
            {
              practice: 'マイグレーションSQLを手動管理する',
              effect:
                'スキーマ定義書とSQLが乖離し、DB構築時にエラーになる。スキーマ定義ルールに連動生成を組み込む',
            },
            {
              practice: 'ルールにテンプレートだけ書いて記載ルールを省略する',
              effect:
                'テンプレートの「形」は守るが「中身の書き方」がバラバラになる。テンプレート + 記載ルール + チェックリストの3点セットで品質を担保する',
            },
            {
              practice: 'proposal.md に記載のないAPIやカラムを勝手に追加する',
              effect:
                '要件定義との整合性が崩れ、usecases.md やbusiness.md と矛盾する。proposalにないものはproposalに追加してから設計書を更新する',
            },
          ]}
        />
      </WorkflowSection>

      {/* セクション9 */}
      <WorkflowSection title="この章で学んだこと" id="section-9" number={9}>
        <WorkflowTable
          headers={['学び', '内容']}
          rows={[
            ['設計書はAIへの仕様伝達手段', 'コード生成の入力として「何を作るか」を明確に定義する'],
            [
              '生成順序が品質を左右する',
              'スキーマ→API仕様書の順で作ることで整合性が自然に保たれる',
            ],
            [
              'proposalがSingle Source of Truth',
              'I/Fの修正はproposalから。生成物の直接修正は整合性を壊す',
            ],
            [
              'PDCAは同じ手法の繰り返し',
              '「要件定義編 — proposal/business生成」で学んだサイクルがそのまま適用できる',
            ],
          ]}
        />

        <WorkflowInfoBox type="preview">
          <Typography variant="body2" fontWeight={600}>
            次の記事: 実装編 — API実装ワークフロー
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            次の記事では以下を行います:
          </Typography>
          <WorkflowList
            items={[
              'api_dev_workflow.md（API実装ワークフローSteering）を作成する',
              '1本のAPIで設計書作成→コード生成→テスト実行の全工程を通しで体験する',
              'ワークフローSteeringを改善しながら残りのAPIを実装する',
            ]}
          />
        </WorkflowInfoBox>
      </WorkflowSection>

      <WorkflowLinkCard
        title="実装編 — API実装ワークフロー"
        description="API実装ワークフローを作成し、設計書→コード生成→単体テストを通しで実行する"
        path="/devdocs/handson/api-workflow"
      />

      <WorkflowChecklist
        groups={[
          {
            title: '最終チェックリスト',
            items: [
              '.kiro/steering/create_schema.md が作成されている',
              '.kiro/steering/api_specs_rule.md が作成されている',
              'docs/schema/ 配下にスキーマ定義書が生成されている',
              'docs/api/ 配下にAPI仕様書が生成されている',
              'src/main/resources/db/migration/ にFlywayマイグレーションSQLが生成されている',
              'セクション5のチェックリスト全項目に合格している',
              '少なくとも1回はルール改善→再生成のサイクルを経験している',
            ],
          },
        ]}
      />
    </HandsonArticleLayout>
  );
};

export default HandsonSchemaApi;
